// T3-B PR 4-bis (2026-05-12):
//   テンプレ motif 一覧 + 編集 + 占い文言プール modal アクセス。
//
// Spec: design/sponsored-motif-spec.md v3 §6.8

import { Button } from '@/components/common/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, BookText } from 'lucide-react';
import TemplateMotifFormSheet from './components/TemplateMotifFormSheet';
import OmikujiTextsPoolSheet from './components/OmikujiTextsPoolSheet';

export default observer(function TemplateMotifManagementPage() {
    const { t } = useTranslation();
    const {
        tierCatalogStore: {
            templateMotifs,
            templateSets,
            listTemplateMotifs,
            listTemplateSets,
            removeTemplateMotif
        },
        modalStore: { showAlertModal }
    } = useStore();

    const [filterSetId, setFilterSetId] = useState<string>('all');
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
    const [editTarget, setEditTarget] = useState<any | null>(null);
    const [poolOpen, setPoolOpen] = useState(false);
    const [poolTarget, setPoolTarget] = useState<any | null>(null);

    const fetchList = () => {
        const filter: any = { include_inactive: 1, include_sponsor_disabled: 1 };
        if (filterSetId && filterSetId !== 'all') filter.template_set_id = filterSetId;
        return flowResult(listTemplateMotifs(filter));
    };

    useEffect(() => {
        flowResult(listTemplateSets());
    }, []);

    useEffect(() => {
        fetchList();
    }, [filterSetId]);

    const handleOpenCreate = () => {
        setSheetMode('create');
        setEditTarget(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (motif: any) => {
        setSheetMode('edit');
        setEditTarget(motif);
        setSheetOpen(true);
    };

    const handleOpenPool = (motif: any) => {
        setPoolTarget(motif);
        setPoolOpen(true);
    };

    const handleDelete = (motif: any) => {
        showAlertModal({
            title: t('words_title.confirm_delete') || '削除確認',
            size: 'sm',
            content: `「${motif.name}」を削除しますか?\n参照中の sponsored_motif / omikuji_texts があると 409 で拒否されます (= is_active=false 代替推奨)。`,
            onSave: async () => {
                const res = await flowResult(removeTemplateMotif(motif.id));
                if (res?.ok) {
                    toastify('success', '削除しました');
                    fetchList();
                } else {
                    toastify('error', res?.message || '削除に失敗しました');
                }
            }
        });
    };

    const parentParam = filterSetId !== 'all' ? filterSetId : undefined;

    return (
        <div className='layout-card'>
            <div className='flex items-center justify-between mb-4'>
                <div>
                    <h2 className='text-[18px] font-bold'>{t('words_title.template_motif_management') || 'テンプレ motif 管理'}</h2>
                    <p className='text-sm text-gray-500 mt-1'>{`登録件数: ${templateMotifs.length}`}</p>
                </div>
                <div className='flex items-center gap-2'>
                    <Select value={filterSetId} onValueChange={setFilterSetId}>
                        <SelectTrigger className='w-[280px]'>
                            <SelectValue placeholder='テンプレートセットで絞り込み' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value='all'>全テンプレートセット</SelectItem>
                            {templateSets.map(ts => (
                                <SelectItem key={ts.id} value={String(ts.id)}>
                                    {ts.name}
                                    {' '}
                                    (
                                    {ts.slug}
                                    )
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button variant='active' onClick={handleOpenCreate}>
                        新規追加
                    </Button>
                </div>
            </div>

            {templateMotifs.length === 0 ?
                (
                    <div className='py-16 text-center text-gray-500'>登録されているテンプレ motif がありません</div>
                ) :
                (
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-gray-200 text-left'>
                                    <th className='py-2 px-3'>ID</th>
                                    <th className='py-2 px-3'>名称</th>
                                    <th className='py-2 px-3'>slug</th>
                                    <th className='py-2 px-3'>テンプレセット</th>
                                    <th className='py-2 px-3'>tier value</th>
                                    <th className='py-2 px-3'>sponsor 禁止</th>
                                    <th className='py-2 px-3'>active</th>
                                    <th className='py-2 px-3 w-[150px]'>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {templateMotifs.map(m => (
                                    <tr key={m.id} className='border-b border-gray-100 hover:bg-gray-50'>
                                        <td className='py-2 px-3 text-gray-500'>{m.id}</td>
                                        <td className='py-2 px-3 font-medium'>{m.name}</td>
                                        <td className='py-2 px-3 font-mono text-xs'>{m.slug}</td>
                                        <td className='py-2 px-3 text-xs'>{m.template_set_name ?? '-'}</td>
                                        <td className='py-2 px-3'>{m.default_tier_value}</td>
                                        {/* 否定形 flag のため ✓ 表示は誤読しやすい → 文字で明示 (2026-07-07 pitfall batch) */}
                                        <td className='py-2 px-3 text-xs'>{m.sponsor_purchase_disabled ? '禁止' : '—'}</td>
                                        <td className='py-2 px-3'>{m.is_active ? '✓' : '×'}</td>
                                        <td className='py-2 px-3'>
                                            <div className='flex gap-2'>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleOpenPool(m)}
                                                    title='占い文言プール'
                                                >
                                                    <BookText className='w-4 h-4' />
                                                </Button>
                                                <Button variant='outline' size='sm' onClick={() => handleOpenEdit(m)}>
                                                    <Pencil className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleDelete(m)}
                                                    className='text-red-600 hover:text-red-800'
                                                >
                                                    <Trash2 className='w-4 h-4' />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            <TemplateMotifFormSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                mode={sheetMode}
                motif={editTarget}
                parentTemplateSetId={parentParam}
                onSuccess={fetchList}
            />

            <OmikujiTextsPoolSheet
                open={poolOpen}
                onOpenChange={setPoolOpen}
                templateMotif={poolTarget}
            />
        </div>
    );
});
